import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Send } from 'lucide-react';
import { useTelegram } from '@/hooks/useTelegram';
import HeroLogo from './HeroLogo';

const BOT_USERNAME =
  (import.meta.env.VITE_BOT_USERNAME as string | undefined) ??
  'neuromaster_agent_bot';
const BOT_URL = `https://t.me/${BOT_USERNAME}`;

interface HeroSectionProps {
  onNavigate: (section: string) => void;
}

export default function HeroSection({ onNavigate }: HeroSectionProps) {
  const { t } = useTranslation();
  const { isInTelegram } = useTelegram();
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.reveal').forEach((el, i) => {
              setTimeout(() => {
                el.classList.add('animate-fade-in-up');
                el.classList.remove('opacity-0');
              }, i * 120);
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="hero"
      className="relative min-h-[100dvh] lg:min-h-[88vh] flex flex-col items-center justify-center px-5 pt-20 pb-24 lg:pt-24 lg:pb-20 overflow-hidden"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-30" style={{ backgroundImage: 'url(/hero-bg.jpg)' }} />
      <div className="absolute inset-0 grid-pattern" />

      {/* Floating glass orbs */}
      <div className="absolute top-[5%] left-[5%] w-[350px] h-[350px] rounded-full animate-float pointer-events-none glass-orb" />
      <div className="absolute bottom-[10%] right-[0%] w-[450px] h-[450px] rounded-full animate-float-slow pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(0,229,160,0.08) 0%, transparent 70%)', filter: 'blur(80px)' }} />
      <div className="absolute top-[35%] left-[35%] w-[300px] h-[300px] rounded-full animate-pulse-orb pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(0,212,255,0.06) 0%, transparent 70%)', filter: 'blur(60px)' }} />

      {/* Content */}
      <div className="relative z-10 text-center max-w-lg lg:max-w-4xl mx-auto">
        {/* Brand mark — speaks for itself, no duplicated wordmark below */}
        <HeroLogo />

        {/* Label pill — single brand surface in the hero */}
        <div className="reveal opacity-0 inline-flex items-center gap-2.5 px-4 py-2 glass-pill rounded-full mb-8 sm:mb-10">
          <span className="w-1.5 h-1.5 rounded-full bg-[#00E5A0] animate-pulse" />
          <p className="text-[#00D4FF] text-[10.5px] sm:text-[11px] font-semibold uppercase tracking-[0.22em]">
            {t('hero.label')}
          </p>
        </div>

        {/* Headline — premium serif scale */}
        <h1 className="reveal opacity-0 stagger-1 text-[42px] sm:text-6xl lg:text-7xl xl:text-[88px] font-extrabold leading-[1.02] tracking-[-0.02em] mb-6 sm:mb-7">
          <span className="text-[#F0F2F5]">{t('hero.title1')}</span>
          <br />
          <span className="text-gradient">{t('hero.title2')}</span>{' '}
          <span className="text-[#F0F2F5]">{t('hero.title3')}</span>
        </h1>

        {/* Subheadline */}
        <p
          className="reveal opacity-0 stagger-2 text-[#B5BBC9] text-[15px] sm:text-[17px] lg:text-lg leading-[1.65] mb-10 sm:mb-12 max-w-sm sm:max-w-xl lg:max-w-2xl mx-auto"
          style={{ fontFamily: "'DM Sans', system-ui, sans-serif" }}
        >
          {t('hero.subtitle')}
        </p>

        {/* CTAs — primary + (browser only) "Open in Telegram" secondary */}
        <div className="reveal opacity-0 stagger-3 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
          <button
            onClick={() => onNavigate('services')}
            className="glass-btn px-12 py-4 sm:py-[18px] text-[#0B0E14] font-bold text-[13px] sm:text-sm tracking-wide rounded-full"
          >
            {t('hero.cta')}
          </button>
          {!isInTelegram && (
            <a
              href={BOT_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-8 py-4 sm:py-[18px] text-white font-bold text-[13px] sm:text-sm tracking-wide rounded-full
                         transition-all duration-300 hover:scale-105 active:scale-95
                         shadow-lg shadow-[rgba(34,158,217,0.35)]"
              style={{
                background:
                  'linear-gradient(135deg, #229ED9 0%, #2AABEE 100%)',
              }}
            >
              <Send size={14} />
              {t('common.openInTelegram')}
            </a>
          )}
        </div>

        {/* Stats — glass cards */}
        <div className="reveal opacity-0 stagger-4 flex flex-wrap items-center justify-center gap-3 sm:gap-4 mt-12 sm:mt-14">
          {(['1', '2', '3'] as const).map((n) => (
            <div
              key={n}
              className="glass-card px-5 sm:px-6 py-4 sm:py-5 rounded-2xl text-center min-w-[112px] sm:min-w-[150px]"
            >
              <p className="text-xl sm:text-2xl lg:text-[26px] font-bold text-gradient leading-none">
                {t(`hero.stat${n}Value`)}
              </p>
              <p className="text-[11px] sm:text-[12px] uppercase tracking-wider text-[#8A93A7] mt-2">
                {t(`hero.stat${n}`)}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom chevron */}
      <button
        onClick={() => onNavigate('services')}
        aria-label={t('hero.cta')}
        className="absolute bottom-6 lg:bottom-10 left-1/2 -translate-x-1/2 text-[#8A93A7] opacity-40 hover:opacity-80 transition-opacity animate-bounce-chevron"
      >
        <ChevronDown size={24} />
      </button>
    </section>
  );
}
